import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
//import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaceIcon from "@mui/icons-material/Place";
import { Database } from "../../lib/database";

type List = Database["public"]["Tables"]["lists"]["Row"];
type Place = Database["public"]["Tables"]["places"]["Row"];

interface ProfileListDetailProps {
  list: List;
  places: Place[];
  loading: boolean;
  onClose: () => void;
  onViewOnMap?: () => void;
}

export const ProfileListDetail = ({
  list,
  places,
  loading,
  onClose,
  onViewOnMap,
}: ProfileListDetailProps) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1}>
            <Typography level="h3" sx={{ fontWeight: "bold" }}>
              {list.title}
            </Typography>
            {list.description && (
              <Typography
                level="body-md"
                sx={{ color: "text.secondary", marginTop: 1 }}
              >
                {list.description}
              </Typography>
            )}
            <Typography
              level="body-sm"
              sx={{ color: "text.tertiary", marginTop: 1 }}
            >
              {places.length} {places.length === 1 ? "place" : "places"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {onViewOnMap && (
              <Button
                variant="soft"
                size="sm"
                startDecorator={<MapIcon />}
                onClick={onViewOnMap}
                data-testid="view-on-map-button"
              >
                View on Map
              </Button>
            )}
            <IconButton
              variant="plain"
              size="sm"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {places.length === 0 ? (
          <Box textAlign="center" py={4}>
            <PlaceIcon sx={{ fontSize: 48, color: "text.tertiary", mb: 1 }} />
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              No places added yet
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {places.map((place) => (
              <Card key={place.id} variant="soft" size="sm">
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box flex={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          level="title-sm"
                          sx={{ fontWeight: "bold" }}
                        >
                          {place.name}
                        </Typography>
                        {/*place.is_visited && (
                          <CheckCircleIcon
                            fontSize="small"
                            color="success"
                            aria-label="visited"
                          />
                        )*/}
                      </Stack>

                      {place.address && (
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.secondary", marginTop: 0.5 }}
                        >
                          {place.address}
                        </Typography>
                      )}

                      {place.notes && (
                        <Typography
                          level="body-sm"
                          sx={{
                            color: "text.secondary",
                            marginTop: 1,
                            fontStyle: "italic",
                          }}
                        >
                          {place.notes}
                        </Typography>
                      )}
                    </Box>

                    {place.category && (
                      <Chip size="sm" variant="outlined">
                        {place.category}
                      </Chip>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
